import { useCallback, useEffect, useRef, useState } from 'react';
import { accessApi } from '../services/accessApi';
import { useAuth } from '../contexts/AuthContext';

/**
 * Loads the current user's latest active personal (resident) access code via
 * GET /api/qr-access/my-codes. If none exists, automatically generates one via
 * POST /api/qr-access/generate (type: 'resident'). The returned code string is
 * the REAL value stored in access_codes — safe to encode in a QR image and
 * validate with GET /api/qr-access/validate/:code.
 */

export interface PersonalQr {
  code: string | null;
  /** Per-code signing secret (qr_secret from my-codes) for the rotating VQR1 payload. */
  qrSecret: string | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

// 2026-09-12 (Jaime policy change): employees (security/maintenance) no longer
// error on My Acceso — they get their OWN personal code stored server-side as
// type='staff', rotating exactly like a resident code (VQR1 + manual digits),
// so gate scans double as their work arrival (clock-in) record.
function isEmployeeWorkContextFn(user: any, contexts: any[]): boolean {
  const condoCtx = (contexts || []).find((c) => c.kind === 'condo');
  const ctxRole = condoCtx?.role || condoCtx?.subType || '';
  if (ctxRole === 'empleado' || ctxRole === 'security') return true;
  return ['empleado', 'security', 'maintenance_staff'].includes(user?.role || '');
}

export function usePersonalQrCode(): PersonalQr {
  const { user, contexts, activeContextId } = useAuth();
  const [code, setCode] = useState<string | null>(null);
  const [qrSecret, setQrSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Guard so a failed generate is not retried in a tight loop across reloads
  // triggered by context changes.
  const generateAttemptedRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await accessApi.qrAccess.myCodes();
      const payload = res.data?.data ?? res.data;
      const list = payload?.codes || (Array.isArray(payload) ? payload : []);
      const normalized = (Array.isArray(list) ? list : []).map((c: any) => ({
        ...c,
        type: c.type || 'resident',
        status: c.status || 'active',
        validUntil: c.valid_until || c.validUntil || '',
      }));
      // my-codes is ordered by created_at DESC — first match is the latest.
      const active = normalized.find(
        (c: any) =>
          (c.type === 'resident' || c.type === 'staff') &&
          c.status === 'active' &&
          c.code &&
          (!c.validUntil || new Date(c.validUntil) > new Date()),
      );
      if (active) {
        setCode(active.code);
        setQrSecret(active.qr_secret || null);
        return;
      }
      // No active personal code — generate one automatically.
      if (generateAttemptedRef.current) return;
      generateAttemptedRef.current = true;
      const activeCtx = contexts.find((c) => c.id === activeContextId);
      const managed = contexts.find((c) => c.kind === 'condo');
      const condoId = activeCtx?.condoId || managed?.condoId || user?.condoId;
      if (!condoId) {
        throw new Error('No se encontró un condominio activo para generar tu código QR.');
      }
      // Employees request their personal code as type 'staff' (2026-09-12);
      // the backend also coerces any personal-type request from an employee
      // work context to 'staff', and keeps denying guest invites.
      const employeeContext = isEmployeeWorkContextFn(user, contexts);
      const gen = await accessApi.qrAccess.generate({ condoId, type: employeeContext ? 'staff' : 'resident' } as any);
      const data = gen.data?.data ?? gen.data;
      const row = data?.code ?? data;
      if (row?.code) {
        setCode(row.code);
        setQrSecret(row.qr_secret || null);
      } else {
        throw new Error('No se pudo generar tu código QR.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Error al cargar tu código QR');
    } finally {
      setLoading(false);
    }
  }, [contexts, activeContextId, user?.condoId]);

  useEffect(() => {
    load();
  }, [load]);

  return { code, qrSecret, loading, error, reload: load };
}

/** QR image URL for a REAL access code (interim renderer until local generation lands). */
// QR visibility fix (2026-09-15, Jaime live QA): force maximum-contrast
// dark-on-white modules and an explicit quiet zone (qzone=3 modules, >= the
// 4-module spec is ideal; 3 keeps scans reliable at small sizes). Previously
// the render depended on the remote service defaults — on the invite landing
// the pass came out faint/tiny and was nearly invisible.
export function qrImageUrl(code: string, size = 300): string {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&qzone=3&color=0-0-0&bgcolor=255-255-255&data=${encodeURIComponent(code)}`;
}
